import React, { useEffect, useState } from "react";
import { Input, Form, InputNumber, Select, Switch } from "antd";
import moment from "moment/moment.js";
import toast from "react-hot-toast";
import useModulePermissions from "../../../hooks/useModulePermissions.jsx";
import columns from "../../../columns/settings/reminder.js";
import TableWithSkeleton from "../../../components/global/table/TableWithSkeleton.jsx";
import Pagination from "../../../components/global/pagination/Pagination.jsx";
import ModalComponent from "../../../components/global/modal/ModalComponent.jsx";
import ConfirmationModal from "../../../components/global/modal/ConfirmationModal.jsx";
import LoadableButton from "../../../components/buttons/LoadableButton.jsx";
import {
  GetEmailRulesApi,
  ActiveInActiveEmailRuleApi,
  CreateEmailRulesApi,
  UpdateEmailRuleApi,
} from "../../../api/request/settings/reminder.js";
import Eye from "../../../assets/Eye.jsx";
import Edit from "../../../assets/Edit.jsx";

const formatApiDate = (value) => {
  if (!value || String(value).startsWith("0001-01-01")) return "--";
  return moment(value).format("DD-MM-YYYY LT");
};

const requirementsLabel = (item) => {
  const parts = [
    item.requiresKyc && "KYC",
    item.requiresDeposit && "Deposit",
    item.requiresLiveAccount && "Live account",
  ].filter(Boolean);
  return parts.length ? parts.join(", ") : "—";
};

const toastApiResponse = (data) => {
  if (data?.success && data?.message) {
    toast.success(data.message);
    return true;
  }
  if (data?.error?.message) {
    toast.error(data.error.message);
    return false;
  }
  return Boolean(data?.success);
};

const toastApiError = (err) => {
  const msg = err?.response?.data?.error?.message;
  if (msg) {
    toast.error(msg);
  }
};

const yesNoOptions = [
  { value: true, label: "Yes" },
  { value: false, label: "No" },
];

export default function Reminder() {
  const reminderPermissions = useModulePermissions('setting');
  const canCreate = !!reminderPermissions.create;
  const canUpdate = !!reminderPermissions.update;

  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecord, setTotalRecord] = useState(0);
  const [search, setSearch] = useState("");
  const [searchText, setSearchText] = useState("");
  const [selectedRule, setSelectedRule] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [ruleDetail, setRuleDetail] = useState(null);
  const [isStatusConfirmOpen, setIsStatusConfirmOpen] = useState(false);
  const [isStatusConfirmLoading, setIsStatusConfirmLoading] = useState(false);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [formSubmitLoading, setFormSubmitLoading] = useState(false);

  const statusHandler = (row) => {
    setSelectedRule(row);
    setIsStatusConfirmOpen(true);
  };

  const openAddModal = () => {
    setIsEditMode(false);
    setEditingRule(null);
    form.resetFields();
    setFormModalOpen(true);
  };

  const openEditModal = (item) => {
    setIsEditMode(true);
    setEditingRule(item);
    form.setFieldsValue({
      triggerEvent: item.triggerEvent,
      daysAfter: item.daysAfter,
      emailTemplate: item.emailTemplate,
      active: !!item.active,
      requiresKyc: !!item.requiresKyc,
      requiresDeposit: !!item.requiresDeposit,
      requiresLiveAccount: !!item.requiresLiveAccount,
      stopEvent: item.stopEvent,
    });
    setFormModalOpen(true);
  };

  const confirmStatusChange = async () => {
    try {
      setIsStatusConfirmLoading(true);
      const params = new URLSearchParams({
        isActive: String(!selectedRule?.active),
        ruleId: selectedRule?.id,
      });
      const { data } = await ActiveInActiveEmailRuleApi(params.toString());
      if (data?.success) {
        toastApiResponse(data);
        setIsStatusConfirmOpen(false);
        setSelectedRule(null);
        fetchRules();
      } else {
        toastApiResponse(data);
      }
      setIsStatusConfirmLoading(false);
    } catch (err) {
      setIsStatusConfirmLoading(false);
      toastApiError(err);
    }
  };

  const onRuleFormFinish = async (values) => {
    try {
      setFormSubmitLoading(true);
      if (isEditMode && editingRule?.id) {
        const payload = {
          id: editingRule.id,
          triggerEvent: values.triggerEvent,
          daysAfter: values.daysAfter,
          emailTemplate: values.emailTemplate,
          active: !!values.active,
          requiresKyc: !!values.requiresKyc,
          requiresDeposit: !!values.requiresDeposit,
          requiresLiveAccount: !!values.requiresLiveAccount,
          stopEvent: values.stopEvent,
        };
        const { data } = await UpdateEmailRuleApi(payload);
        if (data?.success) {
          toastApiResponse(data);
          setFormModalOpen(false);
          fetchRules();
        } else {
          toastApiResponse(data);
        }
      } else {
        const body = [
          {
            triggerEvent: values.triggerEvent,
            daysAfter: values.daysAfter,
            emailTemplate: values.emailTemplate,
            requiresKyc: !!values.requiresKyc,
            requiresDeposit: !!values.requiresDeposit,
            requiresLiveAccount: !!values.requiresLiveAccount,
            stopEvent: values.stopEvent,
          },
        ];
        const { data } = await CreateEmailRulesApi(body);
        if (data?.success) {
          toastApiResponse(data);
          setFormModalOpen(false);
          fetchRules();
        } else {
          toastApiResponse(data);
        }
      }
      setFormSubmitLoading(false);
    } catch (err) {
      setFormSubmitLoading(false);
      toastApiError(err);
    }
  };

  const fetchRules = async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams({
        PageNumber: String(page),
        PageSize: String(pageSize),
        searchKeyword: searchText || "",
      });
      const { data: res } = await GetEmailRulesApi(params.toString());
      if (res?.success) {
        setData(
          (res?.data || []).map((item) => ({
            ...item,
            status: (
              <Switch
                checked={!!item?.active}
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                onChange={(checked) => {
                  if (checked !== !!item.active) {
                    statusHandler(item);
                  }
                }}
              />
            ),
            requirements: requirementsLabel(item),
            createdDate: formatApiDate(item?.createdDate),
            action: (
              <div className="flex items-center gap-3">
                {canUpdate && (
                  <button type="button" onClick={() => openEditModal(item)} aria-label="Edit rule">
                    <Edit />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setRuleDetail(item);
                    setIsDetailsOpen(true);
                  }}
                  aria-label="View rule"
                >
                  <Eye />
                </button>
              </div>
            ),
          }))
        );
        setTotalRecord(res?.totalItems ?? 0);
      } else {
        setData([]);
        setTotalRecord(0);
      }
      setIsLoading(false);
    } catch (e) {
      setData([]);
      setTotalRecord(0);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRules();
  }, [page, pageSize, searchText]);

  useEffect(() => {
    if (!isDetailsOpen) {
      setRuleDetail(null);
    }
  }, [isDetailsOpen]);

  useEffect(() => {
    if (!formModalOpen) {
      form.resetFields();
      setEditingRule(null);
    }
  }, [formModalOpen]);

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:justify-end sm:items-center gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <Input
            size="large"
            style={{ width: 220 }}
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onPressEnter={() => {
              setPage(1);
              setSearchText(search);
            }}
            prefix={
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                <path
                  fillRule="evenodd"
                  d="M10.5 3.75a6.75 6.75 0 1 0 0 13.5 6.75 6.75 0 0 0 0-13.5ZM2.25 10.5a8.25 8.25 0 1 1 14.59 5.28l4.69 4.69a.75.75 0 1 1-1.06 1.06l-4.69-4.69A8.25 8.25 0 0 1 2.25 10.5Z"
                  clipRule="evenodd"
                />
              </svg>
            }
            suffix={
              search && (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  onClick={() => {
                    setPage(1);
                    setSearchText("");
                    setSearch("");
                  }}
                  viewBox="0 0 24 24"
                  fill="#bebebe"
                  className="size-5 cursor-pointer duration-300 hover:fill-[#969595]"
                >
                  <path
                    fillRule="evenodd"
                    d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25Zm-1.72 6.97a.75.75 0 1 0-1.06 1.06L10.94 12l-1.72 1.72a.75.75 0 1 0 1.06 1.06L12 13.06l1.72 1.72a.75.75 0 1 0 1.06-1.06L13.06 12l1.72-1.72a.75.75 0 1 0-1.06-1.06L12 10.94l-1.72-1.72Z"
                    clipRule="evenodd"
                  />
                </svg>
              )
            }
          />
          {canCreate && (
            <button
              type="button"
              onClick={openAddModal}
              className="flex items-center bg-primary text-sm text-white font-semibold px-4 py-2 rounded-lg themeHover duration-500"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 mr-2">
                <path
                  fillRule="evenodd"
                  d="M12 3.75a.75.75 0 0 1 .75.75v6.75h6.75a.75.75 0 0 1 0 1.5h-6.75v6.75a.75.75 0 0 1-1.5 0v-6.75H4.5a.75.75 0 0 1 0-1.5h6.75V4.5a.75.75 0 0 1 .75-.75Z"
                  clipRule="evenodd"
                />
              </svg>
              Add
            </button>
          )}
        </div>
      </div>

      <div className="my-6">
        <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
          <TableWithSkeleton
            columns={columns}
            data={data}
            loading={isLoading}
            hiddenColumns={!canUpdate ? ["Actions", "Status"] : []}
            page={page}
            pageSize={pageSize}
          />
        </div>
        <Pagination total={totalRecord} page={page} setPage={setPage} pageSize={pageSize} setPageSize={setPageSize} />
      </div>

      <ConfirmationModal
        isOpen={isStatusConfirmOpen}
        setIsOpen={setIsStatusConfirmOpen}
        message={`Are you sure you want to ${selectedRule?.active ? "deactivate" : "activate"} this reminder rule?`}
        onConfirm={confirmStatusChange}
        isLoading={isStatusConfirmLoading}
        loadingLabel="Updating status..."
      />

      <ModalComponent
        isOpen={formModalOpen}
        setIsOpen={setFormModalOpen}
        title={isEditMode ? "Edit Reminder Rule" : "Add Reminder Rule"}
        width={800}
      >
        <Form
          className="w-full"
          form={form}
          layout="vertical"
          autoComplete="off"
          onFinish={onRuleFormFinish}
          initialValues={{
            requiresKyc: false,
            requiresDeposit: false,
            requiresLiveAccount: false,
            active: true,
          }}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 px-1">
            <div className="input-white">
              <label className="text-description text-[#2F2B3DCC] font-medium">Trigger event</label>
              <Form.Item name="triggerEvent" rules={[{ required: true, message: "Required" }]}>
                <Input placeholder="e.g. Deposit Done" className="w-full ps-4 py-2" />
              </Form.Item>
            </div>
            <div className="input-white">
              <label className="text-description text-[#2F2B3DCC] font-medium">Days after</label>
              <Form.Item name="daysAfter" type="number" min={0} rules={[{ required: true, message: "Required" }]}>
                <Input type="number" style={{ width: "100%" }} placeholder="Days"  className="w-full ps-4 py-2"/>
              </Form.Item>
            </div>
            <div className="input-white">
              <label className="text-description text-[#2F2B3DCC] font-medium">Email template</label>
              <Form.Item name="emailTemplate" rules={[{ required: true, message: "Required" }]}>
                <Input placeholder="Template name" className="w-full ps-4 py-2" />
              </Form.Item>
            </div>
            <div className="input-white">
              <label className="text-description text-[#2F2B3DCC] font-medium">Stop event</label>
              <Form.Item name="stopEvent" rules={[{ required: true, message: "Required" }]}>
                <Input placeholder="e.g. KYC Completed" className="w-full ps-4 py-2" />
              </Form.Item>
            </div>
            <div className="ant-select-selector-white sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* {isEditMode && ( */}
                <div>
                  <label className="text-description text-[#2F2B3DCC] font-medium">Status</label>
                  <Form.Item name="active" rules={[{ required: true, message: "Required" }]} className="mb-0 mt-1">
                    <Select options={[{ value: true, label: "Active" }, { value: false, label: "Inactive" }]} placeholder="Select" className="w-full" size="large" />
                  </Form.Item>
                </div>
              {/* )} */}
              <div>
                <label className="text-description text-[#2F2B3DCC] font-medium">Requires KYC</label>
                <Form.Item name="requiresKyc" rules={[{ required: true, message: "Required" }]} className="mb-0 mt-1">
                  <Select options={yesNoOptions} placeholder="Select" className="w-full" size="large" />
                </Form.Item>
              </div>
              <div>
                <label className="text-description text-[#2F2B3DCC] font-medium">Requires deposit</label>
                <Form.Item name="requiresDeposit" rules={[{ required: true, message: "Required" }]} className="mb-0 mt-1">
                  <Select options={yesNoOptions} placeholder="Select" className="w-full" size="large" />
                </Form.Item>
              </div>
              <div>
                <label className="text-description text-[#2F2B3DCC] font-medium">Requires live account</label>
                <Form.Item
                  name="requiresLiveAccount"
                  rules={[{ required: true, message: "Required" }]}
                  className="mb-0 mt-1"
                >
                  <Select options={yesNoOptions} placeholder="Select" className="w-full" size="large" />
                </Form.Item>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-8 pb-2">
            <button
              type="button"
              onClick={() => setFormModalOpen(false)}
              className="bg-white border border-primary text-sm primary font-medium uppercase px-8 py-2 rounded-lg hover:bg-[#ffffff] duration-500"
            >
              Cancel
            </button>
            <LoadableButton
              className="bg-primary text-sm text-white font-medium uppercase px-10 py-2 mr-4 rounded-lg themeHover duration-500"
              type="Submit"
              lable={isEditMode ? "Update" : "Save"}
              isLoading={formSubmitLoading}
              loadingLable={isEditMode ? "Updating..." : "Saving..."}
            />

          </div>
        </Form>
      </ModalComponent>

      <ModalComponent isOpen={isDetailsOpen} setIsOpen={setIsDetailsOpen} title="Email Reminder Rule" width={900}>
        <div className="p-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            <div>
              <p className="text-description font-normal">Trigger event</p>
              <p className="text-description primary font-semibold mt-2">{ruleDetail?.triggerEvent || "--"}</p>
            </div>
            <div>
              <p className="text-description font-normal">Days after</p>
              <p className="text-description primary font-semibold mt-2">
                {ruleDetail?.daysAfter != null ? ruleDetail.daysAfter : "--"}
              </p>
            </div>
            <div>
              <p className="text-description font-normal">Email template</p>
              <p className="text-description primary font-semibold mt-2">{ruleDetail?.emailTemplate || "--"}</p>
            </div>
            <div>
              <p className="text-description font-normal">Status</p>
              <p className="text-description primary font-semibold mt-2">{ruleDetail?.active ? "Active" : "Inactive"}</p>
            </div>
            <div>
              <p className="text-description font-normal">Stop event</p>
              <p className="text-description primary font-semibold mt-2">{ruleDetail?.stopEvent || "--"}</p>
            </div>
            <div>
              <p className="text-description font-normal">Requires KYC</p>
              <p className="text-description primary font-semibold mt-2">{ruleDetail?.requiresKyc ? "Yes" : "No"}</p>
            </div>
            <div>
              <p className="text-description font-normal">Requires deposit</p>
              <p className="text-description primary font-semibold mt-2">{ruleDetail?.requiresDeposit ? "Yes" : "No"}</p>
            </div>
            <div>
              <p className="text-description font-normal">Requires live account</p>
              <p className="text-description primary font-semibold mt-2">{ruleDetail?.requiresLiveAccount ? "Yes" : "No"}</p>
            </div>
            <div>
              <p className="text-description font-normal">Created Date</p>
              <p className="text-description primary font-semibold mt-2">{formatApiDate(ruleDetail?.createdDate)}</p>
            </div>
            <div>
              <p className="text-description font-normal">Updated Date</p>
              <p className="text-description primary font-semibold mt-2">{formatApiDate(ruleDetail?.updatedDate)}</p>
            </div>
          </div>
        </div>
      </ModalComponent>
    </div>
  );
}
